import { AxiosError } from 'axios';

/**
 * Extract a user-friendly error message from an error object
 * @param error - The error object (can be AxiosError or generic Error)
 * @param fallbackMessage - Message to use if no specific error found
 * @returns A user-friendly error message
 */
export function getErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (error instanceof AxiosError) {
    // Check for API error response
    const apiError = error.response?.data?.error;
    if (apiError?.message) {
      return apiError.message;
    }

    // Check for standard error message in response
    if (error.response?.data?.message) {
      return error.response.data.message;
    }

    // Network errors
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }

    if (error.code === 'ERR_NETWORK') {
      return 'Network error. Please check your connection.';
    }

    // HTTP status codes
    if (error.response?.status) {
      switch (error.response.status) {
        case 400:
          return 'Invalid request. Please check your input.';
        case 401:
          return 'You are not authenticated. Please log in.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested resource was not found.';
        case 409:
          return 'This operation conflicts with existing data.';
        case 422:
          return 'The provided data is invalid.';
        case 429:
          return 'Too many requests. Please slow down.';
        case 500:
          return 'Server error. Please try again later.';
        case 503:
          return 'Service temporarily unavailable. Please try again later.';
      }
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
}

/**
 * Check if an error is a specific type of API error
 * @param error - The error to check
 * @param code - The error code to check for
 * @returns True if the error matches the code
 */
export function isApiError(error: unknown, code?: string): boolean {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  if (!code) {
    return true;
  }

  return error.response?.data?.error?.code === code;
}

/**
 * Check if an error is a network error
 * @param error - The error to check
 * @returns True if the error is network-related
 */
export function isNetworkError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  return !error.response && Boolean(error.request);
}
